/**
 * Modales de Fina Presta (FINA-3378): único acceso a su storage. Sin PII: solo ids.
 *
 * - Bienvenida (Home, al iniciar sesión, solo dueño): hasta `LINE_MODAL_MAX_VIEWS` vistas **por
 *   ciclo** en `localStorage` (`cr_line_welcome_views_<tenantId>_<ciclo>`). El ciclo es el `_id` del
 *   último crédito completado (o `first` sin ninguno): completar otro crédito abre otra tanda. Su CTA
 *   la retira hasta el próximo ciclo (`retireLineModal` = el máximo). Con el storage bloqueado se da
 *   por agotada: mejor no mostrar un modal que abrirlo en bucle en cada visita.
 * - Préstamo pagado / subida de nivel (vigía global, `LineMilestoneWatcherService`): una sola vez por
 *   crédito (`cr_line_done_seen_<creditoId>`) y el último nivel conocido del comercio
 *   (`cr_line_known_level_<tenantId>`). Con el storage bloqueado se da por visto y no sale, para no
 *   repetirlo en cada navegación.
 * - Marca de sesión en `sessionStorage` (`cr_line_login_modal_checked`, con respaldo en memoria):
 *   el Home evalúa la bienvenida una vez por sesión. `AuthService.logout` la borra con
 *   `clearLoginModalChecked()`.
 */

export const LINE_MODAL_MAX_VIEWS = 3;

/** Ciclo sin crédito completado. */
export const LINE_WELCOME_FIRST_CYCLE = 'first';

export const lineWelcomeViewsKey = (tenantId: string, cycle: string): string => `cr_line_welcome_views_${tenantId}_${cycle}`;
export const lineDoneSeenKey = (creditoId: string): string => `cr_line_done_seen_${creditoId}`;
export const lineKnownLevelKey = (tenantId: string): string => `cr_line_known_level_${tenantId}`;

export function lineModalViews(key: string): number {
    try {
        const n = Number(window.localStorage.getItem(key) ?? 0);
        return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
    } catch {
        return LINE_MODAL_MAX_VIEWS;
    }
}

export function canShowLineModal(key: string): boolean {
    return lineModalViews(key) < LINE_MODAL_MAX_VIEWS;
}

function setLineModalViews(key: string, views: number): void {
    try {
        window.localStorage.setItem(key, String(Math.min(views, LINE_MODAL_MAX_VIEWS)));
    } catch {
        // Sin storage no se recuerda; `lineModalViews` ya da el máximo y el modal no vuelve.
    }
}

/** Suma una vista (el modal quedó montado). */
export function countLineModalView(key: string): void {
    setLineModalViews(key, lineModalViews(key) + 1);
}

/** CTA principal: el modal no vuelve en este ciclo. */
export function retireLineModal(key: string): void {
    setLineModalViews(key, LINE_MODAL_MAX_VIEWS);
}

/** El modal de pagado o nivel de este crédito ya se mostró (o se decidió no mostrarlo). */
export function isLineDoneSeen(creditoId: string): boolean {
    try {
        return window.localStorage.getItem(lineDoneSeenKey(creditoId)) === '1';
    } catch {
        return true;
    }
}

export function markLineDoneSeen(creditoId: string): void {
    try {
        window.localStorage.setItem(lineDoneSeenKey(creditoId), '1');
    } catch {
        // Sin storage `isLineDoneSeen` ya da visto.
    }
}

/** Último nivel de la línea que vio este navegador para el comercio; `null` si no hay. */
export function readKnownLineLevel(tenantId: string): number | null {
    try {
        const raw = window.localStorage.getItem(lineKnownLevelKey(tenantId));
        const n = raw === null ? NaN : Number(raw);
        return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
    } catch {
        return null;
    }
}

export function writeKnownLineLevel(tenantId: string, level: number): void {
    try {
        window.localStorage.setItem(lineKnownLevelKey(tenantId), String(level));
    } catch {
        // Sin storage no se recuerda: la próxima lectura vuelve a sembrarlo sin mostrar nada.
    }
}

const LOGIN_MODAL_CHECKED_KEY = 'cr_line_login_modal_checked';
let loginModalCheckedInMemory = false;

export function isLoginModalChecked(): boolean {
    if (loginModalCheckedInMemory) return true;
    try {
        return window.sessionStorage.getItem(LOGIN_MODAL_CHECKED_KEY) === '1';
    } catch {
        return false;
    }
}

export function markLoginModalChecked(): void {
    loginModalCheckedInMemory = true;
    try {
        window.sessionStorage.setItem(LOGIN_MODAL_CHECKED_KEY, '1');
    } catch {
        // Sin storage queda solo en memoria (hasta recargar).
    }
}

/** Al cerrar sesión: el próximo inicio vuelve a evaluar la bienvenida. */
export function clearLoginModalChecked(): void {
    loginModalCheckedInMemory = false;
    try {
        window.sessionStorage.removeItem(LOGIN_MODAL_CHECKED_KEY);
    } catch {
        // Sin storage no hay nada que limpiar.
    }
}

/** Solo specs: borra todas las claves de los modales de Fina Presta (Karma corre en orden aleatorio). */
export function clearLineModalStorageForTests(): void {
    try {
        Object.keys(window.localStorage)
            .filter(k => /^cr_line_(welcome_views|done_seen|known_level)_/.test(k))
            .forEach(k => window.localStorage.removeItem(k));
    } catch {
        // Sin storage no hay nada que limpiar.
    }
    clearLoginModalChecked();
}
